import React from 'react';
import {
    Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Pagination, Chip, Tooltip, Select, MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { ITask, TaskStatus, TaskPriority } from '../../interfaces/task.interface';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';

type ActorMode = 'assignee' | 'assigner' | 'admin';

interface Props {
    tasks: ITask[];
    loading: boolean;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    mode: ActorMode;
    onStatusChange?: (task: ITask, status: TaskStatus) => void;
    onEdit?: (task: ITask) => void;
    onDelete?: (task: ITask) => void;
}

const priorityColor: Record<TaskPriority, 'default' | 'warning' | 'error'> = {
    LOW: 'default',
    MEDIUM: 'warning',
    HIGH: 'error',
};

const statusColor: Record<TaskStatus, 'default' | 'info' | 'success' | 'warning'> = {
    OPEN: 'default',
    IN_PROGRESS: 'info',
    DONE: 'success',
    CANCELLED: 'warning',
};

const allowedStatuses = (mode: ActorMode, current: TaskStatus): TaskStatus[] => {
    if (mode === 'assignee') return ['OPEN', 'IN_PROGRESS', 'DONE'];
    return ['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'];
};

const TaskListTable: React.FC<Props> = ({ tasks, loading, page, totalPages, onPageChange, mode, onStatusChange, onEdit, onDelete }) => {
    const { t } = useTranslation(['task', 'common']);
    const canEdit = mode === 'assigner' || mode === 'admin';
    const colSpan = 7;

    return (
        <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('taskListTable.columns.title')}</TableCell>
                            <TableCell>{mode === 'assignee' ? t('taskListTable.columns.assignedBy') : t('taskListTable.columns.assignee')}</TableCell>
                            <TableCell>{t('taskListTable.columns.due')}</TableCell>
                            <TableCell>{t('taskListTable.columns.priority')}</TableCell>
                            <TableCell>{t('taskListTable.columns.status')}</TableCell>
                            <TableCell>{t('taskListTable.columns.flags')}</TableCell>
                            <TableCell>{t('taskListTable.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={colSpan}><Typography>{t('loading')}</Typography></TableCell></TableRow>
                        ) : tasks.length === 0 ? (
                            <TableRow><TableCell colSpan={colSpan}><Typography color="text.secondary">{t('taskListTable.empty')}</Typography></TableCell></TableRow>
                        ) : tasks.map(task => (
                            <TableRow key={task.id}>
                                <TableCell>{task.title}</TableCell>
                                <TableCell>{displayUsername(mode === 'assignee' ? task.assignerName : task.assigneeName) || '—'}</TableCell>
                                <TableCell>{task.dueDate ? format(new Date(task.dueDate), 'dd MMM yyyy') : '—'}</TableCell>
                                <TableCell><Chip label={t(`priority.${task.priority}`)} size="small" color={priorityColor[task.priority]} /></TableCell>
                                <TableCell>
                                    {onStatusChange ? (
                                        <Select
                                            size="small"
                                            value={task.status}
                                            onChange={e => onStatusChange(task, e.target.value as TaskStatus)}
                                            disabled={task.status === 'CANCELLED' && mode === 'assignee'}
                                        >
                                            {allowedStatuses(mode, task.status).map(s => (
                                                <MenuItem key={s} value={s}>{t(`status.${s}`)}</MenuItem>
                                            ))}
                                            {!allowedStatuses(mode, task.status).includes(task.status) && (
                                                <MenuItem key={task.status} value={task.status}>{t(`status.${task.status}`)}</MenuItem>
                                            )}
                                        </Select>
                                    ) : (
                                        <Chip label={t(`status.${task.status}`)} size="small" color={statusColor[task.status]} />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {task.isOverdue && <Chip label={t('taskListTable.overdue')} color="error" size="small" />}
                                </TableCell>
                                <TableCell>
                                    {canEdit && onEdit && (
                                        <Tooltip title={t('common:actions.edit')}>
                                            <IconButton onClick={() => onEdit(task)} color="primary"><EditIcon /></IconButton>
                                        </Tooltip>
                                    )}
                                    {canEdit && onDelete && (
                                        <Tooltip title={t('common:actions.delete')}>
                                            <IconButton onClick={() => onDelete(task)} color="error"><DeleteIcon /></IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={page + 1} onChange={(_, p) => onPageChange(p - 1)} />
                </Box>
            )}
        </>
    );
};

export default TaskListTable;
